import React, {
  FC,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import { Card } from 'src/components/Card'
import { Skeleton } from 'src/components/Skeleton'
import { ReactComponent as StarIcon } from 'src/assets/svg/ri-star-fill.svg'
import { ReactComponent as FlashlightIcon } from 'src/assets/svg/ri-flashlight-fill.svg'
import { ReactComponent as AlarmWarningIcon } from 'src/assets/svg/ri-alarm-warning-fill.svg'
import { ReactComponent as BarChartIcon } from 'src/assets/svg/ri-bar-chart-box-fill.svg'
import { ReactComponent as FrameIcon } from 'src/assets/svg/Frame.svg'
import { ReactComponent as EmptyStateStarIcon } from 'src/assets/svg/empty-state-star.svg'
import { ReactComponent as EmptyStateChartIcon } from 'src/assets/svg/empty-state-chart.svg'
import { ReactComponent as BrandTabIcon } from 'src/assets/svg/tabler_tag.svg'
import { ReactComponent as CompetitorsTabIcon } from 'src/assets/svg/category-competitor.svg'
import { ReactComponent as MarketTabIcon } from 'src/assets/svg/globe-grid.svg'
import { ReactComponent as SourceFilterIcon } from 'src/assets/svg/insight-source.svg'
import { ReactComponent as ProjectFilterIcon } from 'src/assets/svg/insight-project.svg'
import { ReactComponent as StrengthsTrophyIcon } from 'src/assets/svg/smart-insights-trophy.svg'
import { ReactComponent as ChallengesPuzzleIcon } from 'src/assets/svg/smart-insights-puzzle.svg'
import { ReactComponent as InsightsBadgeIcon } from 'src/assets/svg/smart-insights-badge.svg'
import { ReactComponent as InsightFlashIcon } from 'src/assets/svg/smart-insights-flash.svg'
import { ReactComponent as PriorityChevronIcon } from 'src/assets/svg/chevron-current.svg'
import { ReactComponent as BackArrowIcon } from 'src/assets/svg/arrow-left-s-line.svg'
import {
  InsightDetailModal,
  InsightDetailData,
  InsightMetricCard,
} from 'src/components/InsightDetailModal'
import {
  useGetSmartInsightsQuery,
  InsightModule,
} from 'src/reactQuery/useGetSmartInsightsQuery'
import { useGetProjectsQuery } from 'src/reactQuery/useGetProjectsQuery'
import { HeaderDropdown } from './HeaderDropdown'
import styles from './smartInsightsAIRecommendations.module.scss'

// Types
export type InsightCategory =
  | 'all'
  // Social-listening taxonomy (HomePage, AIListening, ContentAnalysis)
  | 'brand_health_signals'
  | 'competitor_moves'
  | 'influencer_opportunities'
  // Ad Library taxonomy
  | 'audience_targeting'
  | 'creatives'
  | 'media_strategy'
  | 'messaging_intelligence'
  | 'engagement_reactions'
  // Ad Performance taxonomy (with creatives above)
  | 'budget'
  | 'performance'
  | 'audience'
  | 'tracking'
  // Inbox-analysis taxonomy (InboxAnalysisDetail)
  | 'customer_intent_signals'
  | 'channel_insights'
  | 'automation_opportunities'
  // Review-analysis taxonomy (with crisis_warnings below)
  | 'customer_experience'
  | 'product_service'
  | 'operations'
  | 'pricing_value'
  | 'opportunities'
  // Shared across both
  | 'content_recommendations'
  | 'crisis_warnings'
  | 'comment_analysis'
  // AI Listening prompt deep-dive focus (insight.focus)
  | 'visibility'
  | 'competitive'
  | 'citation'
  | 'content'
  | 'technical_aeo'
  | 'perception'

export type InsightType = 'challenge' | 'strength'

export interface InsightSourceLink {
  date: string
  language: string
  url: string
}

export interface InsightSource {
  links?: InsightSourceLink[]
  platform: string
  text: string
  // AI Listening sources carry the cited URL directly; other modules don't.
  url?: string
}

export type CampaignCategory =
  | 'Brand Management'
  | 'Competitor Analysis'
  | 'Market Research'

// actionType, likelyDriver, observation, priority, searchIntent,
// suggestedExecution and whyItMatters come from the AI Listening backend only.
// Every other module leaves them undefined; the card and modal fall back to the
// plain `details` string exactly as before.
export interface SmartInsight {
  actionType?: string
  aiRecommendation: string
  campaignCategories?: CampaignCategory[]
  category: InsightCategory
  details?: string
  id: string
  isAIRecommendation?: boolean
  isSmartInsight?: boolean
  likelyDriver?: string
  metrics?: {
    mentions: number
    pctPrimary: number
    pctSecondary: number
    // Real campaign denominator (posts/reviews/etc.); optional — older
    // backends send only the percentages.
    total?: number
  }
  module?: InsightModule
  observation?: string
  // Backend-resolved window the insight covers; drives the modal's "Period"
  // line and what fromDate/toDate filter against.
  period?: { from: string; to: string } | null
  priority?: InsightPriority
  searchIntent?: string
  sources?: InsightSource[]
  suggestedExecution?: string
  title: string
  type: InsightType
  whyItMatters?: string
}

export interface InsightFilterTab {
  key: InsightCategory
  label: string
}

// Default tab set — used by social-listening consumers (AIListening, AdLibrary, etc.)
// The InboxAnalysisDetail page passes its own filterTabs prop with the inbox-specific taxonomy.
// comment_analysis is emitted by both content analysis and social listening
// (mentions can be comment-type), so it lives in the default set.
const DEFAULT_FILTER_TABS: InsightFilterTab[] = [
  { key: 'all', label: 'All' },
  { key: 'brand_health_signals', label: 'Brand Health Signals' },
  { key: 'competitor_moves', label: 'Competitor Moves' },
  { key: 'influencer_opportunities', label: 'Influencer Opportunities' },
  { key: 'content_recommendations', label: 'Content Recommendations' },
  { key: 'crisis_warnings', label: 'Crisis Warnings' },
  { key: 'comment_analysis', label: 'Comment Analysis' },
]

// HomePage shares the default taxonomy.
const HOME_PAGE_FILTER_TABS: InsightFilterTab[] = DEFAULT_FILTER_TABS

// Content Analysis shares the default taxonomy (kept as a named export for
// call sites that import it explicitly).
export const CONTENT_ANALYSIS_FILTER_TABS: InsightFilterTab[] =
  DEFAULT_FILTER_TABS

// AI Listening taxonomy — the five categories its backend can emit
// (VALID_CATEGORIES in ai-listening-backend/app/insights.py; anything else is
// normalized to brand_health_signals). comment_analysis is deliberately absent:
// AI Listening has no comments, so the chip would never match an insight.
export const AI_LISTENING_FILTER_TABS: InsightFilterTab[] = [
  { key: 'all', label: 'All' },
  { key: 'brand_health_signals', label: 'Brand Health Signals' },
  { key: 'competitor_moves', label: 'Competitor Moves' },
  { key: 'influencer_opportunities', label: 'Influencer Opportunities' },
  { key: 'content_recommendations', label: 'Content Recommendations' },
  { key: 'crisis_warnings', label: 'Crisis Warnings' },
]

// AI Listening prompt deep-dive taxonomy: the insight's `focus` rather than
// its legacy `category` (callers map focus onto category before passing the
// insights in).
export const AI_LISTENING_FOCUS_FILTER_TABS: InsightFilterTab[] = [
  { key: 'all', label: 'All' },
  { key: 'visibility', label: 'Visibility' },
  { key: 'competitive', label: 'Competitive' },
  { key: 'citation', label: 'Citation' },
  { key: 'content', label: 'Content' },
  { key: 'technical_aeo', label: 'Technical AEO' },
  { key: 'perception', label: 'Perception' },
]

// Ad Library taxonomy — five categories the smart-insights generator emits for
// advertiser analysis (per the swagger comment on SmartInsight.category).
export const AD_LIBRARY_FILTER_TABS: InsightFilterTab[] = [
  { key: 'all', label: 'All' },
  { key: 'audience_targeting', label: 'Audience & Targeting' },
  { key: 'creatives', label: 'Creatives' },
  { key: 'media_strategy', label: 'Media Strategy' },
  { key: 'messaging_intelligence', label: 'Messaging Intelligence' },
  { key: 'engagement_reactions', label: 'Engagement & Reactions' },
]

// Ad Performance (a client's own ad account) — the five categories
// smart-insights-analysis --ad-performance emits; the design's chip row.
export const AD_PERFORMANCE_FILTER_TABS: InsightFilterTab[] = [
  { key: 'all', label: 'All' },
  { key: 'budget', label: 'Budget' },
  { key: 'performance', label: 'Performance' },
  { key: 'creatives', label: 'Creatives' },
  { key: 'audience', label: 'Audience' },
  { key: 'tracking', label: 'Tracking' },
]

// Human labels for the HomePage "Source" (module) filter. Order matches the
// sidebar module order.
const MODULE_LABELS: Record<InsightModule, string> = {
  'ad-analysis': 'Ad Analysis',
  'ai-listening': 'AI Listening',
  'content-analysis': 'Content Analysis',
  'inbox-analysis': 'Inbox Analysis',
  'review-analysis': 'Review Analysis',
  'social-listening': 'Social Listening',
}

const MODULE_ORDER: InsightModule[] = [
  'social-listening',
  'ai-listening',
  'inbox-analysis',
  'content-analysis',
  'review-analysis',
  'ad-analysis',
]

// Scope tabs for the HomePage variant
type ScopeTab = 'brand' | 'competitors' | 'market'

// Maps each HomePage scope tab to the campaign category it represents.
// An insight is included in a scope when its source campaign carries that
// category (campaigns can have multiple categories and fan out into multiple
// tabs). Insights with no campaignCategories (review/content/inbox/ad) fall
// under the default Brand scope — see matchesScope.
const SCOPE_CAMPAIGN_CATEGORY_MAP: Record<ScopeTab, CampaignCategory> = {
  brand: 'Brand Management',
  competitors: 'Competitor Analysis',
  market: 'Market Research',
}

const SCOPE_TABS: {
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  key: ScopeTab
  label: string
}[] = [
  { icon: BrandTabIcon, key: 'brand', label: 'Brand' },
  { icon: CompetitorsTabIcon, key: 'competitors', label: 'Competitors' },
  { icon: MarketTabIcon, key: 'market', label: 'Market' },
]

// Whether an insight belongs in the active HomePage scope tab. Insights that
// carry campaign categories (social listening) match the tab that maps to one
// of their categories. Insights without any category (review/content/inbox/ad
// modules) are surfaced under the default Brand scope so they aren't hidden
// from every tab.
const matchesScope = (
  insight: { campaignCategories?: CampaignCategory[] },
  scopeCampaignCategory: CampaignCategory,
  activeScope: ScopeTab
) => {
  const categories = insight.campaignCategories
  if (Array.isArray(categories) && categories.length > 0) {
    return categories.includes(scopeCampaignCategory)
  }
  return activeScope === 'brand'
}

// Category display config — covers both social-listening and inbox
// taxonomies so either set of tabs renders correctly.
const CATEGORY_CONFIG: Record<
  InsightCategory,
  { icon: React.FC<React.SVGProps<SVGSVGElement>>; label: string }
> = {
  all: { icon: BarChartIcon, label: 'All' },
  audience: { icon: StarIcon, label: 'Audience' },
  audience_targeting: { icon: StarIcon, label: 'Audience & Targeting' },
  automation_opportunities: {
    icon: FlashlightIcon,
    label: 'Automation Opportunities',
  },
  brand_health_signals: { icon: FlashlightIcon, label: 'Brand Health Signals' },
  budget: { icon: AlarmWarningIcon, label: 'Budget' },
  channel_insights: { icon: FrameIcon, label: 'Channel Insights' },
  citation: { icon: StarIcon, label: 'Citation' },
  comment_analysis: { icon: BarChartIcon, label: 'Comment Analysis' },
  competitive: { icon: FrameIcon, label: 'Competitive' },
  competitor_moves: { icon: FrameIcon, label: 'Competitor Moves' },
  content: { icon: BarChartIcon, label: 'Content' },
  content_recommendations: {
    icon: BarChartIcon,
    label: 'Content Recommendations',
  },
  creatives: { icon: FrameIcon, label: 'Creatives' },
  crisis_warnings: { icon: AlarmWarningIcon, label: 'Crisis Warnings' },
  customer_experience: { icon: StarIcon, label: 'Customer Experience' },
  customer_intent_signals: {
    icon: StarIcon,
    label: 'Customer Intent Signals',
  },
  engagement_reactions: { icon: StarIcon, label: 'Engagement & Reactions' },
  influencer_opportunities: {
    icon: StarIcon,
    label: 'Influencer Opportunities',
  },
  media_strategy: { icon: BarChartIcon, label: 'Media Strategy' },
  messaging_intelligence: {
    icon: FlashlightIcon,
    label: 'Messaging Intelligence',
  },
  operations: { icon: FlashlightIcon, label: 'Operations' },
  opportunities: { icon: StarIcon, label: 'Opportunities' },
  perception: { icon: AlarmWarningIcon, label: 'Perception' },
  performance: { icon: BarChartIcon, label: 'Performance' },
  pricing_value: { icon: BarChartIcon, label: 'Pricing & Value' },
  product_service: { icon: FrameIcon, label: 'Product & Service' },
  technical_aeo: { icon: FlashlightIcon, label: 'Technical AEO' },
  tracking: { icon: FrameIcon, label: 'Tracking' },
  visibility: { icon: FlashlightIcon, label: 'Visibility' },
}

// Get icon component for category
const getCategoryIcon = (
  category: InsightCategory
): React.FC<React.SVGProps<SVGSVGElement>> => {
  return CATEGORY_CONFIG[category]?.icon || BarChartIcon
}

// Get the latest date from an insight's source links
const getLatestDate = (insight: SmartInsight): string => {
  const dates = (insight.sources ?? [])
    .flatMap((s) => s.links?.map((l) => l.date) ?? [])
    .filter(Boolean)
    .sort()
  return dates.length > 0 ? dates[dates.length - 1] : ''
}

// Constants for line calculation
const MAX_TOTAL_LINES = 5
const MAX_TITLE_LINES = 3
const LINE_HEIGHT = 20 // Same line-height for both title and subtext (in px)

// Insight Card Component
const InsightCard: FC<{
  insight: SmartInsight
  onClick: (insight: SmartInsight) => void
}> = ({ insight, onClick }) => {
  const isStrength = insight.type === 'strength'
  const IconComponent = getCategoryIcon(insight.category)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const [subtextLines, setSubtextLines] = useState(2) // Default to 2 lines

  // Calculate title lines and set subtext lines dynamically
  useLayoutEffect(() => {
    if (titleRef.current) {
      const titleHeight = titleRef.current.scrollHeight
      const titleLines = Math.min(
        Math.ceil(titleHeight / LINE_HEIGHT),
        MAX_TITLE_LINES
      )
      // Remaining lines for subtext (minimum 1 line)
      const remainingLines = Math.max(1, MAX_TOTAL_LINES - titleLines)
      setSubtextLines(remainingLines)
    }
  }, [insight.title])

  return (
    <div
      className={classNames(styles.insightCard, {
        [styles.challengeCard]: !isStrength,
        [styles.strengthCard]: isStrength,
      })}
      onClick={() => onClick(insight)}
      role="button"
      tabIndex={0}
    >
      <div className={styles.cardHeader}>
        <div
          className={classNames(styles.iconContainer, {
            [styles.challengeIconContainer]: !isStrength,
            [styles.strengthIconContainer]: isStrength,
          })}
        >
          <IconComponent
            className={classNames(styles.icon, {
              [styles.challengeIcon]: !isStrength,
              [styles.strengthIcon]: isStrength,
            })}
          />
        </div>

        <div className={styles.cardContent}>
          <h4 className={styles.cardTitle} ref={titleRef}>
            {insight.title}
          </h4>
          {insight.aiRecommendation && (
            <p
              className={styles.aiRecommendation}
              style={{ WebkitLineClamp: subtextLines }}
            >
              AI Recommendation: {insight.aiRecommendation}
            </p>
          )}
        </div>
      </div>

      <div className={styles.tagsContainer}>
        <div className={styles.tagsLeft}>
          <span
            className={classNames(styles.categoryTag, {
              [styles.challengeCategoryTag]: !isStrength,
              [styles.strengthCategoryTag]: isStrength,
            })}
          >
            <IconComponent className={styles.categoryTagIcon} />
            {CATEGORY_CONFIG[insight.category]?.label}
          </span>
        </div>
      </div>
    </div>
  )
}

// Empty State Component
interface EmptyStateProps {
  category: InsightCategory
  type: 'challenge' | 'strength'
}

const EmptyState: FC<EmptyStateProps> = ({ category, type }) => {
  const categoryLabel = CATEGORY_CONFIG[category]?.label || 'All'
  const typeLabel =
    type === 'strength' ? 'identified strengths' : 'identified challenges'

  return (
    <div className={styles.emptyState}>
      <div className={styles.emptyStateIcon}>
        <EmptyStateStarIcon />
      </div>
      <h4 className={styles.emptyStateTitle}>{categoryLabel}</h4>
      <p className={styles.emptyStateSubtitle}>
        {`You don't have any ${typeLabel} in the\n${categoryLabel} category.`}
      </p>
    </div>
  )
}

// Loading State Component
//
// Mirrors the real two-column layout instead of a small dots box. Previously
// loading rendered only the dots (~170px tall), then the columns replaced it at
// ~600px — a ~430px jump on every load, on every page this widget appears on.
// Dimensions come from smartInsightsAIRecommendations.module.scss: .cardsWrapper
// is locked to 520px ("3 cards * 160px + 2 gaps * 20px"), .insightCard is
// 160px / radius 18. Rendering exactly 3 card skeletons therefore reserves the
// column's true height.
const INSIGHT_SKELETON_COUNT = 3
const INSIGHT_CARD_HEIGHT = 160
const INSIGHT_CARD_RADIUS = 18
const LOADING_COLUMN_TITLES = ['Strengths', 'Challenges']

const LoadingState: FC = () => {
  return (
    <div className={styles.columnsContainer}>
      {LOADING_COLUMN_TITLES.map((title) => (
        <div className={styles.column} key={title}>
          <h3 className={styles.columnTitle}>{title}</h3>
          <div className={styles.cardsWrapper}>
            {Array.from({ length: INSIGHT_SKELETON_COUNT }).map((_, index) => (
              <Skeleton
                height={INSIGHT_CARD_HEIGHT}
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                radius={INSIGHT_CARD_RADIUS}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// Global Empty State Component (when no data at all)
const GlobalEmptyState: FC = () => {
  return (
    <div className={styles.globalEmptyState}>
      <div className={styles.emptyStateIcon}>
        <EmptyStateChartIcon />
      </div>
      <h4 className={styles.emptyStateTitle}>
        Smart Insights & AI Recommendations
      </h4>
      <p className={styles.emptyStateSubtitle}>
        There is not enough data yet to generate Smart Insights
        <br />
        or AI Recommendations. Please check back later.
      </p>
    </div>
  )
}

interface SmartInsightsAIRecommendationsProps {
  campaign?: string
  // Categories of the parent entity (ThemeDetail passes the campaign's,
  // ContentAnalysisDetail the analysis's). Used to hide the competitor_moves
  // chip on Competitor Analysis campaigns/analyses, since the backend never
  // returns competitor_moves insights for those.
  campaignCategories?: string[]
  className?: string
  data?: {
    hasInsights: boolean
    insights?: SmartInsight[]
  }
  // Optional filter-tab override. Defaults to the social-listening
  // taxonomy; InboxAnalysisDetail passes its own inbox-specific set.
  filterTabs?: InsightFilterTab[]
  // Date window forwarded to the API, which keeps insights whose resolved
  // period overlaps it. Omit both to disable date filtering.
  fromDate?: string
  // Hides the "Project" dropdown in the homePageVariant header. The project
  // details page is already scoped to a single project, so filtering by
  // project there is redundant. Other consumers keep the dropdown.
  hideProjectFilter?: boolean
  // When true, applies the updated HomePage design: updated title typography
  // and restyled category filter tabs (no border, purple text + bg on active).
  homePageVariant?: boolean
  isLoading?: boolean
  // Terminology for the detail-modal metrics row. 'social' (default) →
  // "Mention Links" / "Total Discussion"; 'content' → posts; 'inbox' → chats;
  // 'review' → reviews.
  modalMetricsVariant?: ModalMetricsVariant
  // When the insights' citations were produced (AI Listening check date),
  // shown under each citation row in the detail modal.
  modalSourceDate?: string
  // Shows the Brand/Competitors/Market scope tabs + Source/Project dropdowns and
  // enables scope-based filtering. Only meaningful on aggregated dashboards
  // (Home, Projects, Project Details) whose insights span multiple campaign
  // categories. Single-entity pages keep the home layout but omit this row.
  showScopeTabs?: boolean
  // Inclusive end of the date window (see fromDate).
  toDate?: string
}

// ---------------------------------------------------------------------------
// HomePage priority accordions (High / Medium / Low)
//
// `priority` is now returned by the AI Listening backend and used directly (see
// groupByPriority). `mentions` and `%-of-discussion` come from the backend's
// `metrics` — currently review-analysis only. Modules that send none render no
// stats at all; they are never synthesised.
// ---------------------------------------------------------------------------

export type InsightPriority = 'high' | 'low' | 'medium'

// A group is a priority, or the honest bucket for insights that carry none.
// 'none' is what makes partial coverage graceful: one legacy insight without a
// priority no longer collapses the whole column to a flat list, and it is
// never disguised as Medium — that would assert an urgency nobody assigned.
// Ops can fill the bucket at any time by re-running
// sm.back/scripts/backfill-insight-priority.js (it only touches docs still
// missing a valid priority).
export type PriorityGroupKey = InsightPriority | 'none'

const PRIORITY_ORDER: InsightPriority[] = ['high', 'medium', 'low']

// 'none' renders last, and only when it has members (see PriorityGroups).
const GROUP_ORDER: PriorityGroupKey[] = ['high', 'medium', 'low', 'none']

const PRIORITY_LABEL: Record<PriorityGroupKey, string> = {
  high: 'High Priority',
  low: 'Low Priority',
  medium: 'Medium Priority',
  none: 'No Priority',
}

const isRealPriority = (value?: string): value is InsightPriority =>
  value === 'high' || value === 'medium' || value === 'low'

// Whether ANY insight carries a backend-assigned priority. One is enough to
// justify the accordions: prioritized insights group into their real buckets
// and the rest sit visibly under "No Priority". Only a set with no priorities
// at all falls back to the flat recency list — there is nothing to group by.
const hasAnyPriority = (insights: SmartInsight[]): boolean =>
  insights.some((insight) => isRealPriority(insight.priority))

// Rank for ordering: high(0) < medium(1) < low(2) < none(3). Missing sorts
// BELOW low — "unranked last" is a true statement; "medium" is not.
const priorityRank = (value?: string): number =>
  isRealPriority(value) ? PRIORITY_ORDER.indexOf(value) : PRIORITY_ORDER.length

// Order a column: highest priority first, most recent evidence within a
// priority, unranked insights last (by recency among themselves).
//
// Priority used to affect nothing but which accordion an insight landed in —
// both columns sorted purely by recency, so a High-priority insight citing a
// two-week-old post rendered below a Low-priority one citing yesterday. On the
// detail pages (Review, Inbox, AI Listening, Content, Ad), which don't group at
// all, priority was ignored outright.
const sortInsights = (list: SmartInsight[]): SmartInsight[] => {
  const byRecency = (a: SmartInsight, b: SmartInsight) =>
    getLatestDate(b).localeCompare(getLatestDate(a))

  if (!hasAnyPriority(list)) return [...list].sort(byRecency)

  return [...list].sort((a, b) => {
    const rank = priorityRank(a.priority) - priorityRank(b.priority)
    return rank !== 0 ? rank : byRecency(a, b)
  })
}

// Split the insights into the three real buckets plus the honest 'none'.
//
// This previously fell back to bucketing BY POSITION when no insight carried a
// priority — first 50% High, next 30% Medium, rest Low. That rendered list
// order as if it were an urgency judgement (a 5-insight set always showed
// "3 High, 2 Medium"), the same class of fabrication as the hash-derived
// `mentions` placeholder removed earlier. An insight without a priority now
// lands in 'none' — labeled, never silently promoted to Medium.
const groupByPriority = (
  insights: SmartInsight[]
): Record<PriorityGroupKey, SmartInsight[]> => {
  const groups: Record<PriorityGroupKey, SmartInsight[]> = {
    high: [],
    low: [],
    medium: [],
    none: [],
  }
  insights.forEach((insight) => {
    groups[isRealPriority(insight.priority) ? insight.priority : 'none'].push(
      insight
    )
  })
  return groups
}

// Deterministic placeholder metrics from a stable seed (per insight). Some
// modules (e.g. review analysis) don't return an `id`, so guard against an
// undefined/empty seed to avoid crashing on `.length`.
// - mentions: raw count ("187 mentions found")
// - pctPrimary: "% of campaign mentions" / "% of <platform> reviews"
// - pctSecondary: "% of total discussion/posts/chats/reviews"
// NOTE: there is deliberately no placeholder fallback. This used to derive
// mentions/percentages from a hash of the insight id when the backend returned
// none, which rendered as real analytics — an insight reading "received one
// review" displayed "70 mentions". Modules that supply no `metrics` now hide
// the stats instead of inventing them. The backend logs a warning for insights
// whose metrics resolve to zero evidence.

// The whole-campaign total a per-insight count is a share of. PLACEHOLDER:
// derived from the count + its percentage so the two numbers and the donut
// agree (5 of 100 = 5%); swap for the real campaign total once the backend
// returns one.
const totalFromShare = (count: number, pct: number): number =>
  Math.max(count, Math.round((count * 100) / Math.max(pct, 1)))

// Which module's terminology the detail-modal metrics use.
export type ModalMetricsVariant =
  | 'ai'
  | 'content'
  | 'inbox'
  | 'review'
  | 'social'

// Builds the per-module metric cards shown in the detail modal.
const buildModalMetrics = (
  variant: ModalMetricsVariant,
  metrics: {
    mentions: number
    pctPrimary: number
    pctSecondary: number
    // Real campaign denominator. When present it is shown directly; deriving
    // it from the ROUNDED percentage (totalFromShare) said "100" for a
    // 94-post campaign (3 ÷ 3%).
    total?: number
  }
): InsightMetricCard[] => {
  const { mentions, pctPrimary, pctSecondary, total } = metrics
  const n = String(mentions)
  // An integer-rounded 0% reads as "cites nothing" when it actually means
  // "under half a percent" (1 cited mention of a 277-mention campaign). Show
  // <1% whenever there IS evidence but the share rounds to zero.
  const sharePct = (p: number): string =>
    mentions > 0 && p === 0 ? '<1%' : `${p}%`
  switch (variant) {
    case 'content':
      // The insight's own post count, its share as a donut, and the campaign
      // total the share is taken from.
      return [
        {
          chartPercent: pctSecondary,
          stats: [{ emphasis: n, icon: 'link' }],
          title: 'Total Posts for the Insight',
        },
        {
          stats: [
            {
              emphasis: String(total ?? totalFromShare(mentions, pctSecondary)),
              icon: 'bubbles',
              tone: 'dark',
            },
          ],
          title: 'Total Posts in the Campaign',
        },
      ]
    case 'ai':
      // "Cited in N of M AI responses" — M comes from the insight's own check
      // run. No percentage/donut: a share of 4 answers isn't a meaningful %.
      return [
        {
          stats: [{ emphasis: n, icon: 'link', tone: 'dark' }],
          title: 'AI Responses Citing This Insight',
        },
        ...(total
          ? [
              {
                stats: [
                  {
                    emphasis: String(total),
                    icon: 'bubbles' as const,
                    tone: 'dark' as const,
                  },
                ],
                title: 'AI Responses in the Check',
              },
            ]
          : []),
      ]
    case 'inbox':
      return [
        {
          stats: [{ emphasis: n, icon: 'link', tone: 'dark' }],
          title: 'Total Chats for the Insight',
        },
        {
          stats: [
            {
              emphasis: sharePct(pctSecondary),
              icon: 'bubbles',
              label: 'of total chats',
            },
          ],
          title: 'Total Chats in the Campaign',
        },
      ]
    case 'review':
      return [
        {
          stats: [{ emphasis: n, icon: 'link', tone: 'dark' }],
          title: 'Total Reviews for the Insight',
        },
        {
          stats: [
            {
              emphasis: sharePct(pctPrimary),
              icon: 'star',
              label: 'of facility reviews',
            },
            {
              emphasis: sharePct(pctSecondary),
              icon: 'bubbles',
              label: 'of total reviews',
            },
          ],
          title: 'Total Reviews',
        },
      ]
    default:
      return [
        {
          stats: [{ emphasis: n, icon: 'link', tone: 'dark' }],
          title: 'Total Mentions for the Insight',
        },
        {
          stats: [
            {
              emphasis: sharePct(pctPrimary),
              icon: 'megaphone',
              label: 'of campaign mentions',
            },
            {
              emphasis: sharePct(pctSecondary),
              icon: 'bubbles',
              label: 'of total discussion',
            },
          ],
          title: 'Total Discussion',
        },
      ]
  }
}

// Force these priority groups to render their empty state (preview/testing).
// Empty = normal behaviour: a group only shows the empty state when it has no
// insights.
const DEV_FORCE_EMPTY_PRIORITIES: PriorityGroupKey[] = []

const PRIORITY_EMPTY_SUBTEXT =
  'Everything is running smoothly. We’ll highlight high-impact signals and ' +
  'recommendations here as soon as they emerge.'

// Shown inside a priority accordion when that group has no insights.
const PriorityEmptyState: FC<{ priority: PriorityGroupKey }> = ({
  priority,
}) => (
  <div className={styles.priorityEmpty}>
    <div className={styles.priorityEmptyIconWrap}>
      <EmptyStateChartIcon className={styles.priorityEmptyIcon} />
    </div>
    <p className={styles.priorityEmptyTitle}>
      No {PRIORITY_LABEL[priority]} Insights Right Now
    </p>
    <p className={styles.priorityEmptySub}>{PRIORITY_EMPTY_SUBTEXT}</p>
  </div>
)

// On aggregated pages (Home/Projects) one panel renders every module's
// insights, so metric terminology must follow each insight's module — not the
// panel-level prop, which remains the fallback for single-module detail pages
// (their endpoints don't tag `module`).
const VARIANT_BY_MODULE: Record<InsightModule, ModalMetricsVariant> = {
  'ad-analysis': 'social',
  'ai-listening': 'ai',
  'content-analysis': 'content',
  'inbox-analysis': 'inbox',
  'review-analysis': 'review',
  'social-listening': 'social',
}

// Card vocabulary per module, mirroring the modal's metric variants — a
// review insight must not say "mentions ... of total discussion" on its card
// while its modal says "Total Reviews for the Insight".
const CARD_TERMS: Record<
  ModalMetricsVariant,
  { noun: string; share: string | null }
> = {
  // A share of ~4 AI answers is not a meaningful percentage, so the AI card
  // shows counts only — same reasoning as the 'ai' modal variant.
  ai: { noun: 'AI responses', share: null },
  content: { noun: 'posts', share: 'of campaign posts' },
  inbox: { noun: 'chats', share: 'of total chats' },
  review: { noun: 'reviews', share: 'of total reviews' },
  social: { noun: 'mentions', share: 'of total discussion' },
}

const HomeInsightCard: FC<{
  fallbackVariant: ModalMetricsVariant
  insight: SmartInsight
  onClick: (insight: SmartInsight) => void
}> = ({ fallbackVariant, insight, onClick }) => {
  const isStrength = insight.type === 'strength'
  const metrics = insight.metrics
  const terms =
    CARD_TERMS[
      insight.module ? VARIANT_BY_MODULE[insight.module] : fallbackVariant
    ]
  return (
    <div
      className={classNames(styles.homeCard, {
        [styles.homeCardChallenge]: !isStrength,
      })}
      onClick={() => onClick(insight)}
      role="button"
      tabIndex={0}
    >
      <span
        className={classNames(styles.homeCardBar, {
          [styles.homeCardBarChallenge]: !isStrength,
        })}
      />
      <div className={styles.homeCardBody}>
        <h4 className={styles.homeCardTitle}>{insight.title}</h4>
        {(insight.observation ||
          insight.details ||
          insight.aiRecommendation) && (
          <p className={styles.homeCardSub}>
            {/* AI Listening insights compose `details` as markdown, which would
                show literal ** here. Their `observation` is the same opening
                content as plain prose, so prefer it for the card preview. */}
            {insight.observation || insight.details || insight.aiRecommendation}
          </p>
        )}
        <div className={styles.homeCardMeta}>
          <span className={styles.homeCardPill}>
            <InsightFlashIcon className={styles.homeCardPillIcon} />
            {CATEGORY_CONFIG[insight.category]?.label}
          </span>
          {metrics && (
            <div className={styles.homeCardStats}>
              <span className={styles.homeCardStat}>
                <b>{metrics.mentions}</b> {terms.noun}
              </span>
              {terms.share && (
                <span className={styles.homeCardStat}>
                  <b>
                    {metrics.mentions > 0 && metrics.pctSecondary === 0
                      ? '<1%'
                      : `${metrics.pctSecondary}%`}
                  </b>{' '}
                  {terms.share}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const PriorityAccordion: FC<{
  fallbackVariant: ModalMetricsVariant
  insights: SmartInsight[]
  isStrength: boolean
  onCardClick: (insight: SmartInsight) => void
  onToggle: () => void
  open: boolean
  priority: PriorityGroupKey
}> = ({
  fallbackVariant,
  insights,
  isStrength,
  onCardClick,
  onToggle,
  open,
  priority,
}) => (
  <div
    className={classNames(styles.accordion, {
      [styles.accordionChallenge]: !isStrength,
      [styles.accordionOpen]: open,
    })}
  >
    <button
      className={classNames(styles.accordionHeader, {
        [styles.accordionHeaderOpen]: open,
      })}
      onClick={onToggle}
      type="button"
    >
      <span className={styles.accordionTitleGroup}>
        <span
          className={classNames(styles.accordionTitle, {
            [styles.accordionTitleChallenge]: !isStrength,
          })}
        >
          {PRIORITY_LABEL[priority]}
        </span>
        <span className={styles.accordionCount}>
          {insights.length} {insights.length === 1 ? 'Insight' : 'Insights'}
        </span>
      </span>
      <PriorityChevronIcon
        className={classNames(styles.accordionChevron, {
          [styles.accordionChevronOpen]: open,
        })}
      />
    </button>
    {/* Always rendered so open/close animates; collapsed body is height 0.
        The body only clips (overflow:hidden) — the inner element does the
        scrolling, so a short list never flashes a scrollbar mid-animation. */}
    <div className={styles.accordionBody}>
      <div className={styles.accordionScroll}>
        {insights.length === 0 ? (
          <PriorityEmptyState priority={priority} />
        ) : (
          insights.map((insight) => (
            <HomeInsightCard
              fallbackVariant={fallbackVariant}
              insight={insight}
              key={insight.id || insight.title}
              onClick={onCardClick}
            />
          ))
        )}
      </div>
    </div>
  </div>
)

const PriorityGroups: FC<{
  fallbackVariant: ModalMetricsVariant
  insights: SmartInsight[]
  isStrength: boolean
  onCardClick: (insight: SmartInsight) => void
}> = ({ fallbackVariant, insights, isStrength, onCardClick }) => {
  // All groups collapsed by default; each toggles independently.
  const [open, setOpen] = useState<Record<PriorityGroupKey, boolean>>({
    high: false,
    low: false,
    medium: false,
    none: false,
  })
  const grouped = hasAnyPriority(insights)
  // Safe for the empty set too — it yields empty buckets, which is what the
  // accordion empty states render.
  const groups = useMemo(() => groupByPriority(insights), [insights])

  // NO insight in this set carries a priority — show them ungrouped rather
  // than inventing an order. (A partial set DOES group: real buckets plus a
  // visible "No Priority" section, so one legacy insight no longer collapses
  // the whole column.) The empty case keeps the accordions so the column
  // still explains itself.
  if (!grouped && insights.length > 0) {
    return (
      <div
        className={classNames(styles.flatView, {
          [styles.flatViewChallenge]: !isStrength,
        })}
      >
        <div className={styles.flatList}>
          {insights.map((insight) => (
            <HomeInsightCard
              fallbackVariant={fallbackVariant}
              insight={insight}
              key={insight.id || insight.title}
              onClick={onCardClick}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.priorityGroups}>
      {GROUP_ORDER.filter(
        // The three real buckets always render (their empty states explain
        // the column); "No Priority" appears only when it has members.
        (priority) => priority !== 'none' || groups.none.length > 0
      ).map((priority) => (
        <PriorityAccordion
          fallbackVariant={fallbackVariant}
          insights={
            DEV_FORCE_EMPTY_PRIORITIES.includes(priority)
              ? []
              : groups[priority]
          }
          isStrength={isStrength}
          key={priority}
          onCardClick={onCardClick}
          onToggle={() =>
            setOpen((prev) => ({ ...prev, [priority]: !prev[priority] }))
          }
          open={open[priority]}
          priority={priority}
        />
      ))}
    </div>
  )
}

// The "all insights" view shown when the count badge is clicked — one flat,
// scrollable list (no priority grouping), with a link back to the grouped view.
const FlatAllView: FC<{
  fallbackVariant: ModalMetricsVariant
  insights: SmartInsight[]
  isStrength: boolean
  onBack: () => void
  onCardClick: (insight: SmartInsight) => void
}> = ({ fallbackVariant, insights, isStrength, onBack, onCardClick }) => (
  <div
    className={classNames(styles.flatView, {
      [styles.flatViewChallenge]: !isStrength,
    })}
  >
    <button className={styles.flatBack} onClick={onBack} type="button">
      <BackArrowIcon className={styles.flatBackIcon} />
      Back to grouped view
    </button>
    <p className={styles.flatSub}>
      Showing all {insights.length} {isStrength ? 'strength' : 'challenge'}{' '}
      insights, sorted by score
    </p>
    <div className={styles.flatList}>
      {insights.map((insight) => (
        <HomeInsightCard
          fallbackVariant={fallbackVariant}
          insight={insight}
          key={insight.id || insight.title}
          onClick={onCardClick}
        />
      ))}
    </div>
  </div>
)

export const SmartInsightsAIRecommendations: FC<
  SmartInsightsAIRecommendationsProps
> = ({
  campaign,
  campaignCategories,
  className,
  data: externalData,
  filterTabs,
  fromDate,
  hideProjectFilter = false,
  homePageVariant = false,
  isLoading: externalLoading,
  modalMetricsVariant = 'social',
  modalSourceDate,
  showScopeTabs = false,
  toDate,
}) => {
  // Determine which filter tabs to use:
  // - If filterTabs prop is provided, use it
  // - If homePageVariant, use HOME_PAGE_FILTER_TABS
  // - Otherwise, use DEFAULT_FILTER_TABS
  const effectiveFilterTabs =
    filterTabs ??
    (homePageVariant ? HOME_PAGE_FILTER_TABS : DEFAULT_FILTER_TABS)

  // HomePage-only filters. Project scopes the backend query to one project's
  // linked entities; Source filters the returned insights by their module
  // client-side (the backend tags every insight with `module`).
  const [selectedProjectId, setSelectedProjectId] = useState<string>('')
  const [selectedSource, setSelectedSource] = useState<InsightModule | 'all'>(
    'all'
  )

  // Consumers that fetch their own insights (Review, Inbox, AI Listening,
  // Ad Library, Content Analysis, Project Details) pass `data` + `isLoading`.
  // The mode must be decided from the PROPS BEING PRESENT, not from whether
  // data has arrived yet: `enabled: !externalData` fired this global
  // cross-module query during the consumer's own loading window (externalData
  // is undefined until its request resolves), and `externalData ?? query.data`
  // then rendered the user's ENTIRE account's insights on that page whenever
  // the consumer's request was slow, empty, or failed — e.g. content-analysis
  // insights showing up on a review-analysis campaign page.
  const isExternallyManaged =
    externalData !== undefined || externalLoading !== undefined

  const queryResult = useGetSmartInsightsQuery(
    {
      campaign,
      fromDate,
      projectId: showScopeTabs ? selectedProjectId || undefined : undefined,
      toDate,
    },
    { enabled: !isExternallyManaged }
  )

  // Projects power the Home "Project" dropdown; only fetched for that variant.
  const { data: projectsData } = useGetProjectsQuery()
  const projectOptions = useMemo(() => {
    const projects = projectsData?.data ?? []
    return [
      { key: 'all', label: 'All Projects' },
      ...projects.map((p) => ({ key: p._id, label: p.name })),
    ]
  }, [projectsData])

  // Select by mode, never by fallback: in external mode the internal query is
  // disabled, but react-query may still hold cached global data from another
  // page — `externalData ?? queryResult.data` would leak it here.
  const data = isExternallyManaged ? externalData : queryResult.data

  const isLoading = isExternallyManaged
    ? externalLoading ?? false
    : queryResult.isLoading

  const insights = (data?.insights ?? []) as SmartInsight[]

  // Source dropdown lists only modules actually present in the current dataset,
  // plus "All Sources", so there are no dead options.
  const sourceOptions = useMemo(() => {
    const present = new Set(insights.map((i) => i.module).filter(Boolean))
    return [
      { key: 'all', label: 'All Sources' },
      ...MODULE_ORDER.filter((m) => present.has(m)).map((m) => ({
        key: m,
        label: MODULE_LABELS[m],
      })),
    ]
  }, [insights])

  const [activeFilter, setActiveFilter] = useState<InsightCategory>('all')
  const [activeScope, setActiveScope] = useState<ScopeTab>('brand')
  // HomePage: which column (if any) is expanded into the flat "all" view.
  // Clicking a column's count badge sets it; "Back to grouped view" clears it.
  const [expandedColumn, setExpandedColumn] = useState<InsightType | null>(null)

  // Hide the competitor_moves chip when the current view can never contain
  // such an insight: on the HomePage Competitors scope tab, or when the
  // parent is a single Competitor Analysis campaign (backend strips
  // competitor_moves in both cases).
  const hideCompetitorMovesChip =
    (showScopeTabs && activeScope === 'competitors') ||
    (campaignCategories?.includes('Competitor Analysis') ?? false)

  // If a user lands on a view where the chip is hidden but it was their
  // previously active selection, fall back to All so they're not stuck on
  // an invisible filter with zero results.
  useEffect(() => {
    if (hideCompetitorMovesChip && activeFilter === 'competitor_moves') {
      setActiveFilter('all')
    }
  }, [hideCompetitorMovesChip, activeFilter])
  const [selectedInsight, setSelectedInsight] = useState<SmartInsight | null>(
    null
  )

  const getModalData = (insight: SmartInsight): InsightDetailData => {
    const variant = insight.module
      ? VARIANT_BY_MODULE[insight.module]
      : modalMetricsVariant
    return {
      aiRecommendation: insight.aiRecommendation,
      details: insight.details || '',
      isStrength: insight.type === 'strength',
      likelyDriver: insight.likelyDriver,
      // Only when the backend actually supplied figures — the metrics row is
      // omitted entirely otherwise rather than showing invented numbers.
      metrics: insight.metrics
        ? buildModalMetrics(variant, insight.metrics)
        : undefined,
      observation: insight.observation,
      period: insight.period,
      sourceDate: modalSourceDate,
      sources: insight.sources || [],
      suggestedExecution: insight.suggestedExecution,
      title: insight.title,
      whyItMatters: insight.whyItMatters,
    }
  }

  const handleInsightClick = (insight: SmartInsight) => {
    setSelectedInsight(insight)
  }

  const handleCloseModal = () => {
    setSelectedInsight(null)
  }

  // Filter strengths, then order by priority (most recent evidence within a
  // priority; pure recency when the set carries no priority).
  const filteredStrengths = useMemo(() => {
    const scopeCampaignCategory = showScopeTabs
      ? SCOPE_CAMPAIGN_CATEGORY_MAP[activeScope]
      : null

    const filtered = insights.filter((insight) => {
      if (insight.type !== 'strength') return false
      if (
        scopeCampaignCategory &&
        !matchesScope(insight, scopeCampaignCategory, activeScope)
      )
        return false
      if (activeFilter !== 'all' && insight.category !== activeFilter)
        return false
      if (
        showScopeTabs &&
        selectedSource !== 'all' &&
        insight.module !== selectedSource
      )
        return false
      return true
    })
    return sortInsights(filtered)
  }, [activeFilter, activeScope, insights, selectedSource, showScopeTabs])

  // Filter challenges, then order as above.
  const filteredChallenges = useMemo(() => {
    const scopeCampaignCategory = showScopeTabs
      ? SCOPE_CAMPAIGN_CATEGORY_MAP[activeScope]
      : null

    const filtered = insights.filter((insight) => {
      if (insight.type !== 'challenge') return false
      if (
        scopeCampaignCategory &&
        !matchesScope(insight, scopeCampaignCategory, activeScope)
      )
        return false
      if (activeFilter !== 'all' && insight.category !== activeFilter)
        return false
      if (
        showScopeTabs &&
        selectedSource !== 'all' &&
        insight.module !== selectedSource
      )
        return false
      return true
    })
    return sortInsights(filtered)
  }, [activeFilter, activeScope, insights, selectedSource, showScopeTabs])

  const hasNoData =
    !isLoading && (!data || !data.hasInsights || insights.length === 0)

  // Whole-widget empty state when: there's no data at all, OR (HomePage
  // variant) the current view has neither strengths nor challenges. If only
  // ONE column is empty we still render both columns — the empty one shows its
  // High/Medium/Low accordions with their own per-priority empty states.
  const bothColumnsEmpty =
    homePageVariant &&
    filteredStrengths.length === 0 &&
    filteredChallenges.length === 0
  const showGlobalEmptyState = hasNoData || bothColumnsEmpty

  return (
    <Card
      className={`${styles.container} ${homePageVariant ? styles.containerHomePage : ''} ${className || ''}`}
    >
      {/* Header */}
      <div className={styles.header}>
        <h2
          className={classNames(styles.title, {
            [styles.titleHomePage]: homePageVariant,
          })}
        >
          Smart Insights & AI Recommendations
        </h2>
      </div>

      {/* HomePage variant: Brand / Competitors / Market scope tabs, with the
          Source / Project filters right-aligned on the same row, + divider */}
      {showScopeTabs && (
        <>
          <div className={styles.scopeRow}>
            <div className={styles.scopeTabs}>
              {SCOPE_TABS.map(({ icon: Icon, key, label }) => (
                <button
                  className={classNames(styles.scopeTab, {
                    [styles.scopeTabActive]: activeScope === key,
                  })}
                  key={key}
                  onClick={() => setActiveScope(key)}
                  type="button"
                >
                  <Icon className={styles.scopeTabIcon} />
                  {label}
                </button>
              ))}
            </div>

            <div className={styles.headerControls}>
              <HeaderDropdown
                defaultLabel="Source"
                icon={SourceFilterIcon}
                onChange={(key) =>
                  setSelectedSource(key as InsightModule | 'all')
                }
                options={sourceOptions}
                value={selectedSource}
              />
              {!hideProjectFilter && (
                <HeaderDropdown
                  defaultLabel="Project"
                  icon={ProjectFilterIcon}
                  onChange={(key) =>
                    setSelectedProjectId(key === 'all' ? '' : key)
                  }
                  options={projectOptions}
                  value={selectedProjectId || 'all'}
                />
              )}
            </div>
          </div>
          <div className={styles.scopeDivider} />
        </>
      )}

      {/* Filter tabs come from static config, not from the query, so they are
          rendered during loading too. Hiding them would let the row appear late
          and shove the columns down after the data lands. */}
      <div
        className={classNames(styles.filterTabs, {
          [styles.filterTabsHomePage]: homePageVariant,
        })}
      >
        {effectiveFilterTabs
          .filter((tab) => {
            // Competitor Analysis campaigns cannot carry the
            // competitor_moves insight category (the backend strips
            // it), so the chip would be permanently empty.
            if (hideCompetitorMovesChip && tab.key === 'competitor_moves') {
              return false
            }
            return true
          })
          .map((tab) => (
            <button
              className={classNames(styles.filterTab, {
                [styles.active]: activeFilter === tab.key,
                [styles.filterTabHomePage]: homePageVariant,
                [styles.filterTabHomePageActive]:
                  homePageVariant && activeFilter === tab.key,
              })}
              key={tab.key}
              // Clicking the active tab again toggles back to "All".
              onClick={() =>
                setActiveFilter((prev) => (prev === tab.key ? 'all' : tab.key))
              }
              type="button"
            >
              {tab.label}
            </button>
          ))}
      </div>

      {/* Loading shows skeleton columns of the same height; the global empty
          state is a distinct case (loaded, but genuinely nothing to show). */}
      {isLoading ? (
        <LoadingState />
      ) : showGlobalEmptyState ? (
        <GlobalEmptyState />
      ) : (
        /* Columns - show even if filtered data is empty (maintains height) */
        <div className={styles.columnsContainer}>
          {/* Strengths Column */}
          <div
            className={classNames(styles.column, {
              [styles.columnHomePage]: homePageVariant,
            })}
          >
            {homePageVariant ? (
              <div className={styles.columnHeader}>
                <div className={styles.columnTitleGroup}>
                  <StrengthsTrophyIcon className={styles.columnIcon} />
                  <span className={styles.columnTitleHome}>Strengths</span>
                </div>
                <button
                  className={styles.countBadge}
                  onClick={() =>
                    setExpandedColumn((prev) =>
                      prev === 'strength' ? null : 'strength'
                    )
                  }
                  type="button"
                >
                  <InsightsBadgeIcon className={styles.countBadgeIcon} />
                  <span className={styles.countBadgeText}>
                    {filteredStrengths.length} Insights &amp; Recommendations
                  </span>
                </button>
              </div>
            ) : (
              <h3 className={styles.columnTitle}>Strengths</h3>
            )}
            {homePageVariant ? (
              expandedColumn === 'strength' ? (
                <FlatAllView
                  fallbackVariant={modalMetricsVariant}
                  insights={filteredStrengths}
                  isStrength
                  onBack={() => setExpandedColumn(null)}
                  onCardClick={handleInsightClick}
                />
              ) : (
                <PriorityGroups
                  fallbackVariant={modalMetricsVariant}
                  insights={filteredStrengths}
                  isStrength
                  onCardClick={handleInsightClick}
                />
              )
            ) : (
              <div className={styles.cardsWrapper}>
                {filteredStrengths.length === 0 ? (
                  <EmptyState category={activeFilter} type="strength" />
                ) : (
                  filteredStrengths.map((insight) => (
                    <InsightCard
                      insight={insight}
                      key={insight.id || insight.title}
                      onClick={handleInsightClick}
                    />
                  ))
                )}
              </div>
            )}
          </div>

          {/* Challenges Column */}
          <div
            className={classNames(styles.column, {
              [styles.columnHomePage]: homePageVariant,
            })}
          >
            {homePageVariant ? (
              <div className={styles.columnHeader}>
                <div className={styles.columnTitleGroup}>
                  <ChallengesPuzzleIcon className={styles.columnIcon} />
                  <span className={styles.columnTitleHome}>Challenges</span>
                </div>
                <button
                  className={styles.countBadge}
                  onClick={() =>
                    setExpandedColumn((prev) =>
                      prev === 'challenge' ? null : 'challenge'
                    )
                  }
                  type="button"
                >
                  <InsightsBadgeIcon className={styles.countBadgeIcon} />
                  <span className={styles.countBadgeText}>
                    {filteredChallenges.length} Insights &amp; Recommendations
                  </span>
                </button>
              </div>
            ) : (
              <h3 className={styles.columnTitle}>Challenges</h3>
            )}
            {homePageVariant ? (
              expandedColumn === 'challenge' ? (
                <FlatAllView
                  fallbackVariant={modalMetricsVariant}
                  insights={filteredChallenges}
                  isStrength={false}
                  onBack={() => setExpandedColumn(null)}
                  onCardClick={handleInsightClick}
                />
              ) : (
                <PriorityGroups
                  fallbackVariant={modalMetricsVariant}
                  insights={filteredChallenges}
                  isStrength={false}
                  onCardClick={handleInsightClick}
                />
              )
            ) : (
              <div className={styles.cardsWrapper}>
                {filteredChallenges.length === 0 ? (
                  <EmptyState category={activeFilter} type="challenge" />
                ) : (
                  filteredChallenges.map((insight) => (
                    <InsightCard
                      insight={insight}
                      key={insight.id || insight.title}
                      onClick={handleInsightClick}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Insight Detail Modal */}
      {selectedInsight && (
        <InsightDetailModal
          data={getModalData(selectedInsight)}
          onClose={handleCloseModal}
        />
      )}
    </Card>
  )
}
